import { useState, useEffect, useCallback } from 'react';

interface Timeslot {
  start_time: string;
  end_time: string;
}

interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: {
    selectDate: string;
    selectTime: string;
    reviewAndBook: string;
    previousMonth: string;
    nextMonth: string;
    noSlotsAvailable: string;
    yourName: string;
    phoneNumber: string;
    selectedDate: string;
    selectedTime: string;
    modifyTime: string;
    confirmBooking: string;
    bookingSuccess: string;
    bookingError: string;
    back: string;
    loading: string;
    sun: string;
    mon: string;
    tue: string;
    wed: string;
    thu: string;
    fri: string;
    sat: string;
  };
  containerStyle?: React.CSSProperties;
}

export default function BookingCalendar({ 
  timeslotsApi, 
  appointmentsApi, 
  texts,
  containerStyle 
}: BookingCalendarProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);
  const [viewMonth, setViewMonth] = useState<number>(new Date().getMonth());
  const [viewYear, setViewYear] = useState<number>(new Date().getFullYear());
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const dayNames = [texts.sun, texts.mon, texts.tue, texts.wed, texts.thu, texts.fri, texts.sat];

  const fetchTimeslots = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(timeslotsApi);
      const data = await response.json();
      setTimeslots(data || []);
    } catch (error) {
      console.error('Failed to fetch timeslots:', error);
      setTimeslots([]);
    } finally {
      setIsLoading(false);
    }
  }, [timeslotsApi]);

  useEffect(() => {
    fetchTimeslots();
  }, [fetchTimeslots]);

  // Auto-navigate to month with available dates
  useEffect(() => {
    if (timeslots.length > 0 && !selectedDate) {
      const today = new Date().toISOString().split('T')[0];
      const availableDates = [...new Set(timeslots.map(slot => slot.start_time.split(' ')[0]))].sort();
      
      // Find closest available date >= today
      let targetDate = availableDates.find(date => date >= today);
      
      // If no future dates, use the latest available date
      if (!targetDate && availableDates.length > 0) {
        targetDate = availableDates[availableDates.length - 1];
      }

      if (targetDate) {
        const [year, month] = targetDate.split('-').map(Number);
        setViewYear(year);
        setViewMonth(month - 1);
        setSelectedDate(targetDate);
      }
    }
  }, [timeslots, selectedDate]);

  const getAvailableDates = (): Set<string> => {
    return new Set(timeslots.map(slot => slot.start_time.split(' ')[0]));
  };

  const getSlotsForDate = (date: string): Timeslot[] => {
    return timeslots.filter(slot => slot.start_time.split(' ')[0] === date);
  };

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number): number => {
    return new Date(year, month, 1).getDay();
  };

  const formatTime = (datetime: string): string => {
    const timePart = datetime.split(' ')[1];
    if (!timePart) return '';
    const [hours, minutes] = timePart.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getMonthName = (month: number): string => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month];
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
  };

  const handleSlotSelect = (slot: Timeslot) => {
    setSelectedSlot(slot);
    setCurrentStep(2);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(appointmentsApi, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_name: formData.customer_name,
          customer_phone: formData.customer_phone,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          notes: ''
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset state and refetch
        setTimeout(() => {
          setCurrentStep(0);
          setSelectedDate(null);
          setSelectedSlot(null);
          setFormData({ customer_name: '', customer_phone: '' });
          setSubmitStatus(null);
          fetchTimeslots();
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const availableDates = getAvailableDates();
    const days: JSX.Element[] = [];

    // Empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isAvailable = availableDates.has(dateStr);
      const isSelected = selectedDate === dateStr;

      days.push(
        <button
          key={day}
          onClick={() => isAvailable && handleDateSelect(dateStr)}
          disabled={!isAvailable}
          className={`h-10 w-10 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer
            ${isSelected 
              ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30' 
              : isAvailable 
                ? 'bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20' 
                : 'text-slate-600 cursor-not-allowed'
            }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const renderTimeSlots = () => {
    if (!selectedDate) return null;
    const slots = getSlotsForDate(selectedDate);

    if (slots.length === 0) {
      return (
        <p className="text-gray-500 text-center py-8">{texts.noSlotsAvailable}</p>
      );
    }

    return (
      <div className="grid grid-cols-3 gap-2">
        {slots.map((slot, index) => (
          <button
            key={index}
            onClick={() => handleSlotSelect(slot)}
            className="px-3 py-2 bg-indigo-500/10 text-indigo-300 rounded-lg text-sm font-medium hover:bg-indigo-500/20 transition-colors cursor-pointer border border-indigo-500/20 hover:border-indigo-500/40"
          >
            {formatTime(slot.start_time)}
          </button>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={containerStyle} className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-3xl text-indigo-400"></i>
        <span className="ml-3 text-slate-400">{texts.loading}</span>
      </div>
    );
  }

  return (
    <div style={containerStyle} className="bg-slate-900/60 backdrop-blur-xl rounded-2xl shadow-xl border border-slate-700/50 overflow-hidden">
      {/* Steps Indicator */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
        <div className="flex items-center justify-between max-w-md mx-auto">
          {[texts.selectDate, texts.selectTime, texts.reviewAndBook].map((label, index) => (
            <div key={index} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all
                ${currentStep >= index ? 'bg-white text-indigo-600' : 'bg-indigo-400/50 text-white'}`}>
                {index + 1}
              </div>
              {index < 2 && (
                <div className={`w-12 h-0.5 mx-2 ${currentStep > index ? 'bg-white' : 'bg-indigo-400/50'}`}></div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Step 0 & 1: Date and Time Selection */}
        {currentStep < 2 && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Calendar */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">{texts.selectDate}</h3>
              
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button 
                  onClick={handlePrevMonth}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-700/50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-xl text-slate-400"></i>
                </button>
                <span className="font-semibold text-white">
                  {getMonthName(viewMonth)} {viewYear}
                </span>
                <button 
                  onClick={handleNextMonth}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-700/50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xl text-slate-400"></i>
                </button>
              </div>

              {/* Day Names */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map(day => (
                  <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-slate-500">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {renderCalendar()}
              </div>
            </div>

            {/* Time Slots */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">{texts.selectTime}</h3>
              {selectedDate ? (
                <>
                  <p className="text-sm text-slate-400 mb-4">
                    {texts.selectedDate}: <span className="font-medium text-white">{selectedDate}</span>
                  </p>
                  {renderTimeSlots()}
                </>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  {texts.selectDate}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Review and Form */}
        {currentStep === 2 && selectedSlot && (
          <div>
            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-emerald-400"></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{texts.bookingSuccess}</h3>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-white mb-4">{texts.reviewAndBook}</h3>
                
                {/* Review Section */}
                <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">{texts.selectedDate}</p>
                      <p className="font-semibold text-white">{selectedSlot.start_time.split(' ')[0]}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">{texts.selectedTime}</p>
                      <p className="font-semibold text-white">
                        {formatTime(selectedSlot.start_time)} - {formatTime(selectedSlot.end_time)}
                      </p>
                    </div>
                    <button
                      onClick={() => setCurrentStep(0)}
                      className="text-indigo-400 hover:text-indigo-300 text-sm font-medium cursor-pointer"
                    >
                      {texts.modifyTime}
                    </button>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        {texts.yourName} *
                      </label>
                      <input
                        type="text"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleFormChange}
                        required
                        className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        {texts.phoneNumber} *
                      </label>
                      <input
                        type="tel"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleFormChange}
                        required
                        className="w-full px-4 py-3 text-sm bg-slate-800/80 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      />
                    </div>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-300 text-sm">{texts.bookingError}</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(0)}
                      className="px-6 py-3 border border-slate-600/50 text-slate-300 rounded-lg font-medium hover:bg-slate-700/50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {texts.back}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-indigo-600 hover:to-blue-700 transition-all duration-300 disabled:opacity-50 cursor-pointer whitespace-nowrap shadow-lg shadow-indigo-500/25"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          {texts.loading}
                        </span>
                      ) : (
                        texts.confirmBooking
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
